import { forEach, filter, find, map, values } from 'lodash';
import { IModel } from './modelFactory';
import { boolean } from 'yargs';


type Predicate<TModel extends object> = (item:TModel) => boolean;
interface IModelSetFunctions<TModel extends object> extends Iterable<TModel> {
	toDocumentArray(keys?: string[], filter?: string[] | Predicate<TModel>, context?): object[];
	clone(context): IModelSet<TModel>;
	create(data: TModel | object): TModel;
	every(predicate: Predicate<TModel>): boolean;
	get(key: string): TModel;
	set(key: string, data: TModel | object): TModel;
	add(data: TModel | object): TModel;
	delete(key: string): void;
	toArray(): TModel[];
	some(predicate: Predicate<TModel>): boolean;
	sort(compareFn: (a: TModel, b: TModel) => number): TModel[];
	filter(predicate?: Predicate<TModel>): IModelSet<TModel>;
	find(predicate?: Predicate<TModel>):TModel;
	map<TResult>(iteratee?: (item: TModel) => TResult): TResult[];
	length(): number;
}

interface IModelMap<TModel extends object> {
	[key: string]: TModel
}

export type IModelSet<TModel extends object> = IModelSetFunctions<TModel> & IModelMap<TModel>

const modelSetFactory = <TModel extends IModel | object>(arrayVal: TModel[], itemFactory) => {
	const result: IModelSet<TModel> = {} as IModelSet<TModel>;

	forEach(arrayVal, x => {
		const m = (x && typeof x['getModelName'] === 'function') ? x : itemFactory(x);

		result[m.key] = m;
	});

	Object.defineProperty(result, Symbol.iterator, {
		value: () => {
			const items = values(result);
			let index = 0;
			return {
				next: () => {
					if (index < items.length) {
						return { value: items[index++], done: false };
					} else {
						return { done: true };
					}
				}
			}
		}
	});

	Object.defineProperty(result, 'toDocumentArray', {
		value: (k, f, c) => {
			return map(values(result), x => {
				return x['toDocument'] ? x['toDocument'](k, f, c) : JSON.parse(JSON.stringify(x));
			});
		}
	});

	Object.defineProperty(result, 'clone', {
		value: (c) => {
			return modelSetFactory(map(values(result), x => {
				return x['clone'] ? x['clone'](c) : JSON.parse(JSON.stringify(x));
			}), itemFactory);
		}
	});

	Object.defineProperty(result, 'create', {
		value: (docOrModel) => {
			if (typeof docOrModel !== 'undefined') {
				docOrModel = itemFactory(docOrModel);
			}

			return docOrModel;
		}
	});

	Object.defineProperty(result, 'get', {
		value: (key) => {
			return result[key];
		}, configurable: true
	}); // allows for Proxy

	Object.defineProperty(result, 'set', {
		value: (key, docOrModel) => {
			if (typeof docOrModel === 'undefined') {
				(result as any).delete(key);
			}
			else {
				docOrModel = (typeof docOrModel.getModelName === 'function') ? docOrModel : (result as any).create(docOrModel);
				result[key] = docOrModel;
			}

			return docOrModel;
		}, configurable: true
	}); // allows for Proxy

	Object.defineProperty(result, 'add', {
		value: (docOrModel) => {
			if (typeof docOrModel !== 'undefined') {
				docOrModel = (typeof docOrModel.getModelName === 'function') ? docOrModel : (result as any).create(docOrModel);
				result[docOrModel.key] = docOrModel;
			}

			return docOrModel;
		}, configurable: true
	}); // allows for Proxy

	Object.defineProperty(result, 'delete', {
		value: (key) => {
			// eslint-disable-next-line no-prototype-builtins
			if (result.hasOwnProperty(key)) {
				delete result[key];
			}
		}, configurable: true
	}); // allows for Proxy

	Object.defineProperty(result, 'every', {
		value: (predicate) => {
			return values(result).every(predicate);
		}
	});

	Object.defineProperty(result, 'toArray', {
		value: () => {
			return values(result);
		}
	});

	Object.defineProperty(result, 'sort', {
		value: (predicate) => {
			return values(result).sort(predicate);
		}
	});

	Object.defineProperty(result, 'some', {
		value: (predicate) => {
			return values(result).some(predicate);
		}
	});

	Object.defineProperty(result, 'find', {
		value: (predicate) => {
			return find(values(result), predicate);
		}
	});

	Object.defineProperty(result, 'filter', {
		value: (predicate) => {
			return modelSetFactory(filter(values(result), predicate), itemFactory);
		}
	});

	Object.defineProperty(result, 'map', {
		value: (iteratee) => {
			return map(values(result), iteratee);
		}
	});

	Object.defineProperty(result, 'length', {
		value: () => {
			return values(result).length;
		}
	});

	return result;
};

export default modelSetFactory;